import clsx from 'clsx';
import {
  Children,
  Fragment,
  forwardRef,
  isValidElement,
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';

import { useAriaLabelingProps } from '../../core/hooks/useAriaLabelingProps.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useId } from '../../core/hooks/useId.js';
import { isSlot } from '../../core/utils/is-slot.js';
import { devLog } from '../../core/utils/logging.js';
import { Form } from '../../forms/form/Form.js';
import { FormMessages } from '../../forms/form/FormMessages.js';
import { showToast } from '../../notifications/toast/Toast.js';
import { FilterField } from '../filter-field/FilterField.js';
import { SegmentSelector } from '../segment-selector/SegmentSelector.js';
import { TimeframeSelector } from '../timeframe-selector/TimeframeSelector.js';
import { FilterCompositionContext } from './contexts/FilterCompositionContext.js';
import * as styles from './FilterComposition.sty.js';
import type { FilterCompositionProps } from './FilterComposition.types.js';
import { createFilterCompositionStore } from './store/createFilterCompositionStore.js';
import { registerFilterCompositionById } from './store/filterCompositionRegistry.js';
import { FilterCompositionApplyButton } from './sub-components/FilterCompositionApplyButton.js';
import { FilterCompositionFilterField } from './sub-components/FilterCompositionFilterField.js';
import { FilterCompositionSegmentSelector } from './sub-components/FilterCompositionSegmentSelector.js';
import { FilterCompositionTimeframeSelector } from './sub-components/FilterCompositionTimeframeSelector.js';

const COMPONENT_NAME = 'FilterComposition';

/**
 * Returns the canonical rendering rank for a known built-in slot element.
 * Unknown / custom children return -1.
 */
function canonicalRank(child: ReactElement): number {
  if (isSlot(child, FilterCompositionSegmentSelector)) {
    return 0;
  }
  if (isSlot(child, FilterCompositionFilterField)) {
    return 1;
  }
  if (isSlot(child, FilterCompositionTimeframeSelector)) {
    return 2;
  }
  if (isSlot(child, FilterCompositionApplyButton)) {
    return 3;
  }
  return -1;
}

function isBuiltInSlot(child: ReactNode): child is ReactElement {
  return isValidElement(child) && canonicalRank(child) !== -1;
}

function flattenChildren(children: ReactNode): ReactElement[] {
  const result: ReactElement[] = [];
  Children.forEach(children, (child) => {
    if (!isValidElement(child)) {
      return;
    }
    if (child.type === Fragment) {
      const fragment = child as ReactElement<{ children?: ReactNode }>;
      flattenChildren(fragment.props.children).forEach((fragmentChild) =>
        result.push(fragmentChild),
      );
      return;
    }
    result.push(child);
  });
  return result;
}

function isUnsupportedDirectControl(child: ReactElement): boolean {
  return (
    child.type === FilterField ||
    child.type === SegmentSelector ||
    child.type === TimeframeSelector
  );
}

/**
 * Sorts built-in slot children into their canonical positions while keeping
 * custom children interleaved at their original JSX positions.
 */
function orderChildren(children: ReactNode): ReactNode[] {
  const all = flattenChildren(children).filter((child) => {
    if (!isUnsupportedDirectControl(child)) {
      return true;
    }
    devLog(
      '[FilterComposition] Direct filter controls are not supported as children. Use the corresponding FilterComposition.* slot instead.',
    );
    return false;
  });

  const builtInPositions = new Set<number>();
  const builtIns: ReactElement[] = [];
  all.forEach((child, index) => {
    if (isBuiltInSlot(child)) {
      builtInPositions.add(index);
      builtIns.push(child);
    }
  });

  builtIns.sort((a, b) => canonicalRank(a) - canonicalRank(b));

  let builtInCursor = 0;
  return all.map((child, index) => {
    if (builtInPositions.has(index)) {
      return builtIns[builtInCursor++];
    }
    return child;
  });
}

/**
 * Splits children into named slots and five custom-child zones (A–E) based on
 * their JSX position relative to the built-in slots:
 *
 * - Zone A: before SegmentSelector
 * - Zone B: between SegmentSelector and FilterField
 * - Zone C: between FilterField and TimeframeSelector
 * - Zone D: between TimeframeSelector and ApplyButton
 * - Zone E: after ApplyButton
 */
function splitChildren(ordered: ReactNode[]) {
  let segmentSelectorChild: ReactNode = null;
  let timeframeSelectorChild: ReactNode = null;
  let filterFieldChild: ReactNode = null;
  let applyButtonChild: ReactNode = null;
  const groupA: ReactNode[] = [];
  const groupB: ReactNode[] = [];
  const groupC: ReactNode[] = [];
  const groupD: ReactNode[] = [];
  const groupE: ReactNode[] = [];

  let zone: 'A' | 'B' | 'C' | 'D' | 'E' = 'A';

  const reportDuplicate = (slotName: string) =>
    devLog(
      `[FilterComposition] FilterComposition.${slotName} must not be rendered more than once.`,
    );

  for (const child of ordered) {
    if (isSlot(child, FilterCompositionSegmentSelector)) {
      if (segmentSelectorChild === null) {
        segmentSelectorChild = child;
      } else {
        reportDuplicate('SegmentSelector');
      }
      zone = 'B';
    } else if (isSlot(child, FilterCompositionFilterField)) {
      if (filterFieldChild === null) {
        filterFieldChild = child;
      } else {
        reportDuplicate('FilterField');
      }
      zone = 'C';
    } else if (isSlot(child, FilterCompositionTimeframeSelector)) {
      if (timeframeSelectorChild === null) {
        timeframeSelectorChild = child;
      } else {
        reportDuplicate('TimeframeSelector');
      }
      zone = 'D';
    } else if (isSlot(child, FilterCompositionApplyButton)) {
      if (applyButtonChild === null) {
        applyButtonChild = child;
      } else {
        reportDuplicate('ApplyButton');
      }
      zone = 'E';
    } else if (zone === 'A') {
      groupA.push(child);
    } else if (zone === 'B') {
      groupB.push(child);
    } else if (zone === 'C') {
      groupC.push(child);
    } else if (zone === 'D') {
      groupD.push(child);
    } else {
      groupE.push(child);
    }
  }

  return {
    segmentSelectorChild,
    customGroupA: groupA,
    customGroupB: groupB,
    customGroupC: groupC,
    customGroupD: groupD,
    customGroupE: groupE,
    timeframeSelectorChild,
    filterFieldChild,
    applyButtonChild,
  };
}

const _FilterComposition = /* @__PURE__ */ forwardRef<
  HTMLDivElement,
  FilterCompositionProps
>(
  (
    {
      id,
      _store: storeProp,
      _onApply: onApply,
      _onCancel: onCancel,
      _loading: loading,
      _onValidityChange: onValidityChange,
      _formHandle,
      children,
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      'aria-describedby': ariaDescribedBy,
      ...remainingProps
    }: FilterCompositionProps,
    forwardedRef,
  ) => {
    const [behavioralTrackingProps, restProps] =
      useBehavioralTrackingProps(remainingProps);
    const ariaLabelingProps = useAriaLabelingProps({
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      'aria-describedby': ariaDescribedBy,
    });
    const generatedId = useId();

    // Lazy `useState` initializer: creates the fallback store exactly once per
    // mount, and never when a `_store` prop is supplied for the whole lifetime.
    const [internalStore] = useState(() =>
      createFilterCompositionStore({ id: id ?? generatedId }),
    );
    const store = storeProp ?? internalStore;
    const formId = store.id;

    const intl = useIntl();
    const showCancellationToast = useCallback(() => {
      showToast({
        title: intl.formatMessage({
          defaultMessage: 'The request was cancelled.',
          id: 'FyckX6XbAY31huMU',
          description:
            'Toast shown when a filter composition apply is cancelled from apply or refresh state.',
        }),
      });
    }, [intl]);

    useEffect(() => {
      store._configure({
        onApply,
        onCancel,
        loading,
        showCancellationToast: showCancellationToast,
      });
      return () => store._configure({});
    }, [store, onApply, onCancel, loading, showCancellationToast]);

    // Publishes the store so components outside this subtree — a menu item in a
    // portalled table cell menu, for instance — can reach it by id.
    useEffect(() => registerFilterCompositionById(store.id, store), [store]);

    useEffect(() => {
      if (_formHandle !== undefined && _formHandle !== store._form) {
        devLog(
          '[FilterComposition] `_formHandle` must reference the composition store Form runtime.',
        );
      }
    }, [_formHandle, store]);

    useEffect(() => {
      if (id !== undefined && id !== store.id) {
        devLog(
          `[FilterComposition] The \`id\` prop ("${id}") differs from \`store.id\` ("${store.id}").`,
        );
      }
    }, [id, store]);

    const {
      segmentSelectorChild,
      customGroupA,
      customGroupB,
      customGroupC,
      customGroupD,
      customGroupE,
      timeframeSelectorChild,
      filterFieldChild,
      applyButtonChild,
    } = splitChildren(orderChildren(children));

    const contextValue = useMemo(
      () => ({ id: formId, store }),
      [formId, store],
    );

    const rootRef = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
      // Checked for truthiness rather than against `null`: the optional chain
      // yields `undefined` when the root is detached or not yet portalled, and
      // `!== null` would fire this warning precisely when there is no ancestor
      // form. `closest` only ever returns an element or `null`, so there is no
      // falsy-but-present case to worry about.
      const ancestorForm = rootRef.current?.parentElement?.closest('form');
      if (ancestorForm) {
        devLog(
          '[FilterComposition] FilterComposition renders its own <form>; wrapping it in another <form> results in invalid nested-form markup. Associate outer form controls via the native `form="…"` attribute instead.',
        );
      }
    }, []);

    const setRootRef = (node: HTMLDivElement | null) => {
      rootRef.current = node;
      if (typeof forwardedRef === 'function') {
        forwardedRef(node);
      } else if (forwardedRef !== null) {
        forwardedRef.current = node;
      }
    };

    const hasCustomGroupA = customGroupA.length > 0;
    const hasCustomGroupB = customGroupB.length > 0;
    const hasCustomGroupC = customGroupC.length > 0;
    const hasCustomGroupD = customGroupD.length > 0;
    const hasCustomGroupE = customGroupE.length > 0;
    const hasRow2 = filterFieldChild !== null;

    if (applyButtonChild === null) {
      devLog(
        '[FilterComposition] FilterComposition.ApplyButton is required but was not found among children.',
      );
    }

    return (
      <FilterCompositionContext.Provider value={contextValue}>
        <div
          {...restProps}
          ref={setRootRef}
          className={clsx(styles.FilterCompositionRoot, consumerClassName)}
          style={consumerStyle}
          data-testid={dataTestId}
          data-dtrum-mask={dataDtrumMask}
          data-dtrum-allow={dataDtrumAllow}
          {...behavioralTrackingProps}
          data-dt-component={COMPONENT_NAME}
        >
          <Form
            id={formId}
            form={store._form}
            onValidityChange={onValidityChange}
            className={styles.FilterCompositionForm}
            {...ariaLabelingProps}
          >
            <div className={styles.FilterCompositionLayout}>
              {hasCustomGroupA && (
                <div
                  className={clsx(
                    styles.FilterCompositionCustomGroupA,
                    hasRow2 && styles.FilterCompositionRow1Item,
                    'strato-filter-composition__zone--a',
                  )}
                >
                  {customGroupA}
                </div>
              )}
              {segmentSelectorChild !== null && (
                <div
                  className={clsx(
                    styles.FilterCompositionSegmentsSlot,
                    hasRow2 && styles.FilterCompositionRow1Item,
                  )}
                >
                  {segmentSelectorChild}
                </div>
              )}
              {hasCustomGroupB && (
                <div
                  className={clsx(
                    styles.FilterCompositionCustomGroupB,
                    hasRow2 && styles.FilterCompositionRow1Item,
                    'strato-filter-composition__zone--b',
                  )}
                >
                  {customGroupB}
                </div>
              )}
              {filterFieldChild !== null && (
                <div className={styles.FilterCompositionFilterFieldSlot}>
                  {filterFieldChild}
                </div>
              )}
              {hasCustomGroupC && (
                <div
                  className={clsx(
                    styles.FilterCompositionCustomGroupC,
                    hasRow2 && styles.FilterCompositionRow1Item,
                    'strato-filter-composition__zone--c',
                  )}
                >
                  {customGroupC}
                </div>
              )}
              {timeframeSelectorChild !== null && (
                <div
                  className={clsx(
                    styles.FilterCompositionTFSSlot,
                    hasRow2 && styles.FilterCompositionRow1Item,
                  )}
                >
                  {timeframeSelectorChild}
                </div>
              )}
              {hasCustomGroupD && (
                <div
                  className={clsx(
                    styles.FilterCompositionCustomGroupD,
                    hasRow2 && styles.FilterCompositionRow1Item,
                    'strato-filter-composition__zone--d',
                  )}
                >
                  {customGroupD}
                </div>
              )}
              {filterFieldChild !== null && (
                <div className={styles.FilterCompositionTabletRowBreak} />
              )}
              {applyButtonChild}
              {hasCustomGroupE && (
                <div
                  className={clsx(
                    styles.FilterCompositionCustomGroupE,
                    'strato-filter-composition__zone--e',
                  )}
                >
                  {customGroupE}
                </div>
              )}
            </div>
            <FormMessages />
          </Form>
        </div>
      </FilterCompositionContext.Provider>
    );
  },
);

(
  _FilterComposition as typeof _FilterComposition & { displayName: string }
).displayName = COMPONENT_NAME;

/**
 * Compound component that arranges `SegmentSelector`, `FilterField`,
 * `TimeframeSelector`, and `ApplyButton` in the standard order with a built-in
 * responsive layout.
 *
 * Built-in slots are always rendered in their canonical order regardless of
 * their JSX position. Any custom children that are not built-in slots are
 * interleaved based on their JSX position relative to the built-in slots,
 * falling into one of five zones:
 *
 * - Zone A: before `SegmentSelector`
 * - Zone B: between `SegmentSelector` and `FilterField`
 * - Zone C: between `FilterField` and `TimeframeSelector`
 * - Zone D: between `TimeframeSelector` and `ApplyButton`
 * - Zone E: after `ApplyButton`
 *
 * @remarks
 * The responsive placement of the built-in slots is fixed. Custom-child zones
 * follow a default responsive order, but a consumer may relocate their own
 * custom zone at the tablet or mobile breakpoint by setting the corresponding
 * order variable on the composition (targeting the
 * `strato-filter-composition__zone--{a..e}` class hooks, or inline):
 * `--strato-fc-zone-<zone>-order-{tablet,mobile}`. Order defaults are spaced by
 * 10, so e.g. `--strato-fc-zone-b-order-mobile: 15` places zone B between the
 * SegmentSelector (10) and TimeframeSelector (20) on mobile. This changes visual
 * order only; DOM and tab order stay canonical.
 *
 * @example
 * ```tsx
 * <FilterComposition id="my-filter">
 *   <CustomToolbarStart />
 *   <FilterComposition.SegmentSelector />
 *   <CustomZoneBContent />
 *   <FilterComposition.FilterField />
 *   <CustomZoneCContent />
 *   <FilterComposition.TimeframeSelector />
 *   <CustomZoneDContent />
 *   <FilterComposition.ApplyButton />
 *   <CustomToolbarEnd />
 * </FilterComposition>
 * ```
 *
 * @public
 */
export const FilterComposition = Object.assign(_FilterComposition, {
  FilterField: FilterCompositionFilterField,
  SegmentSelector: FilterCompositionSegmentSelector,
  TimeframeSelector: FilterCompositionTimeframeSelector,
  ApplyButton: FilterCompositionApplyButton,
});
